'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Calendar, Users, Sparkles, ChevronRight, Clock, CheckCircle } from 'lucide-react';
import Link from '../../components/Link';
import { checkLoginStatus, getUserData } from '../../api/authService';

export default function ClientBooking() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState<any>(null);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        const checkAuth = async () => {
            const auth = await checkLoginStatus();
            setIsLoggedIn(auth);

            if (auth) {
                const userData = await getUserData();
                setUser(userData);
                setIsAdmin(userData?.roles?.includes('administrator'));
            }
        };

        checkAuth();
    }, []);

    const handleLogout = async () => {
        try {
            localStorage.removeItem('token');
            window.location.href = '/';
        } catch (err) {
            console.error('Logout error:', err);
        }
    };

    const services = [
        {
            icon: Heart,
            title: 'Vaginismus Recovery',
            description: 'Compassionate support and guidance for overcoming vaginismus with personalized therapeutic approaches.',
            color: '#CEACA1'
        },
        {
            icon: Sparkles,
            title: 'Clarity Sessions',
            description: 'Deep dive conversations to gain clarity on your personal journey and find direction.',
            color: '#AAB09D'
        },
        {
            icon: CheckCircle,
            title: 'VOV Recovery',
            description: 'Specialized support for Vaginismus, Orgasm, and Vulvodynia recovery with expert guidance.',
            color: '#CEACA1'
        },
        {
            icon: Users,
            title: 'Couples Counseling',
            description: 'Professional counseling for couples looking to strengthen their relationship and communication.',
            color: '#AAB09D'
        }
    ];

    return (
        <div className="min-h-screen bg-[#ffffff]">

            {/* Hero Section */}
            <div className="relative bg-gradient-to-b from-gray-900/80 via-gray-900/60 to-black border-b border-[#ECE7E8]">
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-[url('https://tsoministries.com/wp-content/uploads/2025/09/B_H00742-scaled.jpeg')] bg-cover bg-center md:bg-[center_50%]"></div>
                    <div className="absolute inset-0 bg-black opacity-35"></div>
                    {/* <div className="absolute inset-0 bg-[linear-gradient(135deg,#ECE7E8_0%,#FAF9F8_100%)] opacity-35"></div> */}
                    <div className="absolute inset-0 backdrop-blur-xl"></div>
                </div>

                <div className="relative max-w-7xl mx-auto py-40 px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <h2 className="text-[3.5rem] font-bold mb-6 text-[#ffffff] tracking-[-0.03em] leading-[1.2]">
                            Welcome to The Healing Pavilion
                        </h2>

                        <p className="text-[1.375rem] text-gray-200 mb-12 max-w-[700px] mx-auto leading-[1.6] font-normal">
                            A safe, compassionate space for emotional healing, spiritual growth, and relational restoration
                        </p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="flex gap-4 justify-center"
                        >
                            <Link to={isLoggedIn ? "/client-booking" : "/login"}>
                                <motion.button
                                    whileHover={{ scale: 1.05, boxShadow: '0 8px 24px rgba(206, 172, 161, 0.4)' }}
                                    whileTap={{ scale: 0.98 }}
                                    className="px-10 py-4 bg-[#CEACA1] text-white rounded-xl cursor-pointer text-lg font-semibold flex items-center gap-2 transition-all duration-300"
                                >
                                    {isLoggedIn ? 'Book Your Session' : 'Begin Your Journey'}
                                    {isLoggedIn ? <Calendar size={20} /> : <ChevronRight size={20} />}
                                </motion.button>
                            </Link>
                        </motion.div>

                    </motion.div>
                </div>
            </div>

            {/* About Section */}
            <div className="bg-white">
                <div className="max-w-7xl mx-auto py-20 px-8">

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="max-w-[900px] mx-auto text-center"
                    >
                        <h3 className="text-[2.5rem] font-bold mb-8 text-[#2d2d2d] tracking-[-0.02em]">
                            About Us
                        </h3>

                        <p className="text-lg text-gray-600 leading-[1.8] mb-6">
                            The Healing Place is a counseling and psychotherapy center dedicated to helping individuals, couples, and families achieve emotional health and personal clarity. We provide a safe, confidential, and nonjudgmental environment where people can explore life's challenges, heal from past experiences, and develop healthier ways of thinking, relating, and living.
                        </p>

                        <p className="text-lg text-gray-600 leading-[1.8]">
                            Our services are delivered by professionally trained therapists who are Christocentric believers, integrating clinical excellence with compassion, strong values, and respect for each person's journey toward wholeness.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="grid md:grid-cols-2 gap-12 mt-16"
                    >
                        <div className="p-10 bg-[#ECE7E8] rounded-2xl border-l-4 border-[#CEACA1]">
                            <h4 className="text-2xl font-semibold mb-4 text-[#2d2d2d]">
                                Our Mission
                            </h4>
                            <p className="text-base text-gray-600 leading-[1.7]">
                                 At The Healing Place, our mission is to provide a safe, compassionate, and Christ-centered environment where individuals can experience emotional healing, spiritual growth, and relational restoration. We are committed to delivering evidence-based counselling, therapy, and psychotherapy services that integrate Christocentric principles with professional expertise, empowering our clients to live a life of purpose, hope, and wholeness.
                            </p>
                        </div>

                        <div className="p-10 bg-[#ECE7E8] rounded-2xl border-l-4 border-[#AAB09D]">
                            <h4 className="text-2xl font-semibold mb-4 text-[#2d2d2d]">
                                Our Vision
                            </h4>
                            <p className="text-base text-gray-600 leading-[1.7]">
                                Our vision is to see individuals and communities living in emotional balance, self-awareness, and well-being. Through psychotherapy, we aim to foster wholeness by helping people integrate their experiences, restore inner stability, and move forward with clarity, confidence, and purpose.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Services Section */}
            <div className="bg-[#FAF9F8]">
                <div className="max-w-7xl mx-auto py-20 px-8">

                    <motion.h3
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="text-[2.5rem] font-bold mb-12 text-center text-[#2d2d2d] tracking-[-0.02em]"
                    >
                        Our Services
                    </motion.h3>

                    <div className="grid md:grid-cols-2 gap-8">
                        {services.map((service, index) => {
                            const IconComponent = service.icon;

                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    whileHover={{ y: -8, boxShadow: '0 12px 32px rgba(0,0,0,0.08)' }}
                                    className="p-10 border border-[#ECE7E8] rounded-2xl bg-white transition-all duration-300 cursor-pointer"
                                >
                                    <motion.div
                                        whileHover={{ scale: 1.1, rotate: 5 }}
                                        className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                                        style={{ backgroundColor: service.color + '20' }}
                                    >
                                        <IconComponent size={28} color={service.color} strokeWidth={2} />
                                    </motion.div>

                                    <h4 className="text-[1.375rem] font-semibold mb-3 text-[#2d2d2d]">
                                        {service.title}
                                    </h4>

                                    <p className="text-gray-500 leading-[1.7] text-base">
                                        {service.description}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Booking Info */}
            <div className="bg-white border-t border-[#ECE7E8]">
                <div className="max-w-7xl mx-auto py-20 px-8">

                    <motion.h3
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="text-[2.5rem] font-bold mb-12 text-center text-[#2d2d2d] tracking-[-0.02em]"
                    >
                        Booking Information
                    </motion.h3>

                    <div className="grid md:grid-cols-3 gap-10">

                        {[
                            { icon: Calendar, color: '#CEACA1', title: 'Tue, Wed, Fri', text: 'Available booking days' },
                            { icon: Clock, color: '#AAB09D', title: '9 AM - 5 PM', text: 'Typical booking hours' },
                            { icon: Heart, color: '#CEACA1', title: '1 Hour', text: 'Session duration' }
                        ].map((item, i) => {
                            const Icon = item.icon;

                            return (
                                <motion.div
                                    key={i}
                                    whileHover={{ scale: 1.05 }}
                                    className="text-center p-10 bg-[#ECE7E8] rounded-2xl transition-all duration-300"
                                >
                                    <div className="w-18 h-18 rounded-full bg-white flex items-center justify-center mx-auto mb-6">
                                        <Icon size={36} color={item.color} strokeWidth={2} />
                                    </div>

                                    <div className="text-2xl font-bold mb-3 tracking-[-0.02em]" style={{ color: item.color }}>
                                        {item.title}
                                    </div>

                                    <p className="text-gray-500 text-base font-medium">
                                        {item.text}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>

                </div>
            </div>
        </div>
    );
}
